import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Lock,
  Sparkles,
  Sun,
  DollarSign,
  Tag,
  Users,
  Globe,
  TrendingUp,
  TrendingDown,
  Check,
  X,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Cell,
  CartesianGrid,
} from "recharts";
import {
  openSubscribeGate,
  isSubscribed as isVisitorSubscribed,
  isOwnerMode,
  hasPremiumFeatures,
} from "../../lib/subscription";

/* ------------------------------------------------------------------ */
/*  Tier detection — read a signal from localStorage / URL.            */
/*  Real users get their tier from the backend subscription record;    */
/*  for now we accept a `?tier=premium` query hint for demo/testing.   */
/* ------------------------------------------------------------------ */
export function getSubscriptionTier() {
  if (typeof window === "undefined") return "trial";
  const url = new URL(window.location.href);
  const q = url.searchParams.get("tier");
  if (q) {
    window.localStorage.setItem("eviq.subscription_tier", q);
    return q;
  }
  return window.localStorage.getItem("eviq.subscription_tier") || "trial";
}

export function isPremium() {
  // Owner-mode always unlocks. Any real paid tier (basic/yearly/lifetime)
  // and an active preview also unlock. The old ?tier=premium hint is kept
  // as a manual demo override.
  if (isOwnerMode()) return true;
  if (hasPremiumFeatures()) return true;
  if (isVisitorSubscribed()) return true;
  return getSubscriptionTier() === "premium";
}

/* ------------------------------------------------------------------ */
/*  Shared shell: header + Premium gate                                */
/* ------------------------------------------------------------------ */
const PremiumHeader = ({ title, icon: Icon, color, back }) => (
  <div className="flex items-center gap-2 pt-4 pb-2">
    <button
      onClick={back}
      className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/5"
      aria-label="Back"
      data-testid="mobile-app-subview-back"
    >
      <ChevronLeft className="w-4 h-4 text-zinc-400" strokeWidth={2.5} />
    </button>
    <div
      className="w-7 h-7 rounded-lg flex items-center justify-center border shrink-0"
      style={{ background: `${color}15`, borderColor: `${color}40` }}
    >
      <Icon className="w-4 h-4" style={{ color }} strokeWidth={2} />
    </div>
    <span className="text-sm text-white font-semibold">{title}</span>
    <span
      className="ml-auto font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
      style={{ color: "#FFB020", borderColor: "rgba(255,176,32,0.4)", background: "rgba(255,176,32,0.08)" }}
    >
      Premium
    </span>
  </div>
);

const PremiumGate = ({ title, teaser, onUpgrade, testid }) => (
  <div className="space-y-3 pt-2" data-testid={testid}>
    <div
      className="rounded-2xl border p-5 text-center overflow-hidden relative"
      style={{
        borderColor: "rgba(255,176,32,0.35)",
        background: "linear-gradient(180deg, rgba(255,176,32,0.08), rgba(255,176,32,0.02))",
      }}
    >
      <div
        className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-3 border"
        style={{ borderColor: "rgba(255,176,32,0.4)", background: "rgba(255,176,32,0.1)" }}
      >
        <Lock className="w-6 h-6" style={{ color: "#FFB020" }} strokeWidth={2} />
      </div>
      <div className="font-display text-lg text-white font-semibold mb-1">
        {title}
      </div>
      <div className="text-[12px] text-zinc-300 leading-relaxed">{teaser}</div>
    </div>

    <button
      onClick={onUpgrade || (() => openSubscribeGate("premium-view"))}
      className="w-full brand-gradient text-black font-semibold rounded-xl py-3 text-sm inline-flex items-center justify-center gap-2"
      data-testid="premium-upgrade-cta"
    >
      Start 7-day free trial · then $99.99/yr
      <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
    </button>

    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
        Included in Premium
      </div>
      <ul className="space-y-1.5">
        {[
          "Weather-aware charging recommendations",
          "Lifetime cost-savings vs gas tracking",
          "Marketplace value + comparable listings",
          "Cohort benchmarking against your vintage",
          "Fleet-wide percentile ranking",
        ].map((f) => (
          <li key={f} className="flex items-start gap-2 text-[11px] text-zinc-300">
            <Check className="w-3 h-3 mt-0.5 shrink-0" style={{ color: "#39FF14" }} strokeWidth={3} />
            {f}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  1. Weather-aware charging                                          */
/* ------------------------------------------------------------------ */
export const WeatherChargingView = ({ back, vehicle, climate, onUpgrade }) => {
  if (!isPremium()) {
    return (
      <div className="pb-4">
        <PremiumHeader title="Weather-aware charging" icon={Sun} color="#FFB020" back={back} />
        <PremiumGate
          title="Charge smarter with weather"
          teaser="Match your charge sessions to temperature + off-peak rates. See when your pack is happiest — and when it's costing you range."
          onUpgrade={onUpgrade}
          testid="premium-locked-weather"
        />
      </div>
    );
  }

  // Live premium content
  const hourlyEfficiency = [
    { hr: "6a", eff: 88, temp: 58 },
    { hr: "9a", eff: 95, temp: 68 },
    { hr: "12p", eff: 92, temp: 78 },
    { hr: "3p", eff: 85, temp: climate.summer_high_f || 85 },
    { hr: "6p", eff: 90, temp: 75 },
    { hr: "9p", eff: 97, temp: 65 },
    { hr: "12a", eff: 98, temp: 58 },
    { hr: "3a", eff: 96, temp: 54 },
  ];
  const bestHr = hourlyEfficiency.reduce((a, b) => (a.eff > b.eff ? a : b));

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-weather">
      <PremiumHeader title="Weather-aware charging" icon={Sun} color="#FFB020" back={back} />

      <div
        className="rounded-xl border p-4"
        style={{ borderColor: "rgba(57,255,20,0.25)", background: "rgba(57,255,20,0.05)" }}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#39FF14]">
            Best charge window today
          </div>
          {vehicle?.live && (
            <span
              data-testid="weather-live-badge"
              className="inline-flex items-center gap-1 text-[8px] font-mono uppercase tracking-[0.14em] text-[#39FF14]"
              title={`Live from vehicle GPS · updated ${vehicle.liveAgeSec ?? 0}s ago`}
            >
              <span className="w-1 h-1 rounded-full bg-[#39FF14] animate-pulse" aria-hidden="true" />
              Live
            </span>
          )}
        </div>
        <div className="font-display text-3xl text-white font-semibold">
          {bestHr.hr}
          <span className="text-base text-zinc-400 font-mono ml-2">— {bestHr.eff}% eff</span>
        </div>
        <div className="text-[11px] text-zinc-400 mt-1">
          {climate.label} · ambient {bestHr.temp}°F when you plug in
        </div>
        {vehicle?.live && vehicle.location?.lat != null && (
          <div
            data-testid="weather-vehicle-location"
            className="font-mono text-[9px] text-zinc-500 mt-1.5 pt-1.5 border-t border-white/[0.06]"
          >
            📍 Vehicle at {vehicle.location.lat.toFixed(3)}, {vehicle.location.lng.toFixed(3)}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <div className="flex items-baseline justify-between mb-2 px-1">
          <span className="font-mono text-[10px] uppercase text-zinc-500">
            Charge efficiency · next 24h
          </span>
          <span className="font-mono text-[10px] text-zinc-500">% accepted</span>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={hourlyEfficiency} margin={{ top: 5, right: 4, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="effGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFB020" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#FFB020" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#ffffff08" vertical={false} />
            <XAxis dataKey="hr" tick={{ fill: "#71717a", fontSize: 9 }} axisLine={false} tickLine={false} />
            <YAxis domain={[70, 100]} tick={{ fill: "#71717a", fontSize: 9 }} axisLine={false} tickLine={false} width={22} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 10 }} />
            <Area type="monotone" dataKey="eff" stroke="#FFB020" strokeWidth={2} fill="url(#effGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-2">
        {[
          { title: "Pre-condition pack before DC charging", body: "Warm to 95°F+ to reduce cell plating in cold weather", icon: Sun, color: "#FFB020" },
          { title: "Shift to off-peak window (9pm – 6am)", body: "Better efficiency + off-peak electricity rates", icon: TrendingDown, color: "#39FF14" },
          { title: "Avoid supercharging in direct summer sun", body: "Pack heat + ambient heat compounds cell aging", icon: Sun, color: "#FF6B57" },
        ].map((tip, i) => {
          const Icon = tip.icon;
          return (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border" style={{ background: `${tip.color}15`, borderColor: `${tip.color}44` }}>
                <Icon className="w-4 h-4" style={{ color: tip.color }} strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-medium">{tip.title}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed">{tip.body}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  2. Cost savings                                                    */
/* ------------------------------------------------------------------ */
export const CostSavingsView = ({ back, vehicle, onUpgrade }) => {
  if (!isPremium()) {
    return (
      <div className="pb-4">
        <PremiumHeader title="Cost savings" icon={DollarSign} color="#39FF14" back={back} />
        <PremiumGate
          title="See every dollar you didn't spend on gas"
          teaser="Lifetime savings tracking, per-mile comparison, off-peak vs on-peak breakdown, and a monthly bill you can share with your CFO (or spouse)."
          onUpgrade={onUpgrade}
          testid="premium-locked-cost"
        />
      </div>
    );
  }

  const milesDriven = vehicle.odometer;
  const gasEquivalentPrice = 3.85; // avg US gas $/gal
  const gasMpg = 32;   // similar-class ICE
  const kwhPerMi = 0.28;
  const homeKwhCost = 0.14;
  const gasCost = (milesDriven / gasMpg) * gasEquivalentPrice;
  const evCost = milesDriven * kwhPerMi * homeKwhCost;
  const saved = Math.round(gasCost - evCost);

  const yearly = [
    { yr: "2021", saved: Math.round(saved * 0.1) },
    { yr: "2022", saved: Math.round(saved * 0.28) },
    { yr: "2023", saved: Math.round(saved * 0.5) },
    { yr: "2024", saved: Math.round(saved * 0.76) },
    { yr: "2025", saved: Math.round(saved) },
  ];

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-cost">
      <PremiumHeader title="Cost savings" icon={DollarSign} color="#39FF14" back={back} />

      <div
        className="rounded-xl border p-4"
        style={{ borderColor: "rgba(57,255,20,0.28)", background: "linear-gradient(180deg, rgba(57,255,20,0.08), transparent)" }}
      >
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#39FF14] mb-1">
          Lifetime savings vs gas
        </div>
        <div className="font-display text-4xl text-white font-semibold" data-testid="cost-savings-total">
          ${saved.toLocaleString()}
        </div>
        <div className="text-[11px] text-zinc-400 mt-1">
          Over {milesDriven.toLocaleString()} mi · ${(saved / (milesDriven / 1000)).toFixed(0)}/1000 mi
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <div className="flex items-baseline justify-between mb-2 px-1">
          <span className="font-mono text-[10px] uppercase text-zinc-500">Yearly savings</span>
          <span className="font-mono text-[10px] text-zinc-500">USD</span>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={yearly} margin={{ top: 5, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#ffffff08" vertical={false} />
            <XAxis dataKey="yr" tick={{ fill: "#71717a", fontSize: 9 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#71717a", fontSize: 9 }} axisLine={false} tickLine={false} width={28} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 10 }} formatter={(v) => [`$${v}`, "Saved"]} />
            <Bar dataKey="saved" fill="#39FF14" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatBox label="Gas equivalent" value={`$${Math.round(gasCost).toLocaleString()}`} color="#FF6B57" />
        <StatBox label="EV cost" value={`$${Math.round(evCost).toLocaleString()}`} color="#39FF14" />
      </div>
    </div>
  );
};

const StatBox = ({ label, value, color }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
    <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="font-display text-xl text-white font-semibold mt-1" style={{ color }}>
      {value}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  3. Marketplace value                                               */
/* ------------------------------------------------------------------ */
export const MarketplaceView = ({ back, vehicle, onUpgrade }) => {
  if (!isPremium()) {
    return (
      <div className="pb-4">
        <PremiumHeader title="Marketplace value" icon={Tag} color="#00E5FF" back={back} />
        <PremiumGate
          title="Know your car's true resale value"
          teaser="Live comparables from major EV marketplaces, adjusted for your specific SoH, warranty room, and mileage. See how much your EVIQ score is worth in dollars."
          onUpgrade={onUpgrade}
          testid="premium-locked-market"
        />
      </div>
    );
  }

  // Base MSRP by model
  const baseMsrp = {
    "model-s": 89000,
    "model-3": 46000,
    "model-x": 98000,
    "model-y": 51000,
    "cybertruck": 82000,
  }[vehicle.modelKey] || 50000;

  const age = new Date().getFullYear() - vehicle.year;
  const rawDepreciation = Math.min(0.75, age * 0.12);
  const sohBonus = (vehicle.sohCurrent - 85) * 0.003; // +/- from SoH
  const estValue = Math.round(baseMsrp * (1 - rawDepreciation) * (1 + sohBonus));
  const kbbAvg = Math.round(estValue * 0.94);
  const withEVIQBonus = Math.round(estValue * 1.06);

  const comps = [
    { label: "Similar VIN — no report", val: kbbAvg, color: "#FF6B57" },
    { label: "Your car — estimate", val: estValue, color: "#00E5FF" },
    { label: "Your car w/ EVIQ report", val: withEVIQBonus, color: "#39FF14" },
  ];

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-market">
      <PremiumHeader title="Marketplace value" icon={Tag} color="#00E5FF" back={back} />

      <div className="rounded-xl border border-[#00E5FF]/25 bg-[#00E5FF]/[0.04] p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#00E5FF] mb-1">
          Current estimated value
        </div>
        <div className="font-display text-4xl text-white font-semibold" data-testid="market-value-current">
          ${estValue.toLocaleString()}
        </div>
        <div className="text-[11px] text-zinc-400 mt-1">
          {vehicle.year} {vehicle.short} · {vehicle.sohCurrent}% SoH · {vehicle.odometer.toLocaleString()} mi
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2 px-1">
          Value comparison
        </div>
        <div className="space-y-2">
          {comps.map((c) => (
            <div key={c.label} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-[11px] text-zinc-300">{c.label}</span>
                <span className="font-mono text-sm text-white">${c.val.toLocaleString()}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${(c.val / withEVIQBonus) * 100}%`, background: c.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#39FF14]/25 bg-[#39FF14]/[0.04] p-3">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2} />
          <span className="text-sm text-white font-semibold">
            +${(withEVIQBonus - kbbAvg).toLocaleString()} with an EVIQ resale report
          </span>
        </div>
        <div className="text-[11px] text-zinc-400 leading-relaxed">
          Buyers pay 4–7% more for VIN-verified battery health data. Premium
          plan includes a shareable, buyer-ready PDF.
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  4. Cohort benchmark                                                */
/* ------------------------------------------------------------------ */
export const CohortView = ({ back, vehicle, onUpgrade }) => {
  if (!isPremium()) {
    return (
      <div className="pb-4">
        <PremiumHeader title="Cohort benchmark" icon={Users} color="#00E5FF" back={back} />
        <PremiumGate
          title="How you stack up against your cohort"
          teaser="Compare your SoH, degradation rate, and charging habits against every other Tesla of your model, year, and mileage bucket."
          onUpgrade={onUpgrade}
          testid="premium-locked-cohort"
        />
      </div>
    );
  }

  const cohortSize = 1284 + Math.round(vehicle.year * 0.7);
  const percentile = Math.round(50 + (vehicle.sohCurrent - 85) * 2.4);
  const clampedPct = Math.max(5, Math.min(97, percentile));

  const radarData = [
    { metric: "SoH", you: Math.round(vehicle.sohCurrent), cohort: 87.2 },
    { metric: "Slope", you: 100 - Math.abs(vehicle.delta30d) * 100, cohort: 80 },
    { metric: "Charging", you: 76, cohort: 72 },
    { metric: "Climate", you: 92, cohort: 84 },
    { metric: "Thermal", you: 86, cohort: 78 },
    { metric: "Range", you: (vehicle.currentRange / vehicle.epaRange) * 100, cohort: 88 },
  ];

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-cohort">
      <PremiumHeader title="Cohort benchmark" icon={Users} color="#00E5FF" back={back} />

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
          Your cohort · {vehicle.year} {vehicle.short}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-4xl text-white font-semibold" data-testid="cohort-percentile">
            {clampedPct}
            <span className="text-base text-zinc-400 font-mono ml-1">
              {clampedPct >= 80 ? "th" : clampedPct >= 20 ? "th" : "th"} pctl
            </span>
          </span>
          <span
            className="ml-auto font-mono text-[10px] uppercase px-2 py-0.5 rounded-full border"
            style={{
              color: clampedPct >= 60 ? "#39FF14" : clampedPct >= 40 ? "#FFB020" : "#FF6B57",
              borderColor: clampedPct >= 60 ? "rgba(57,255,20,0.4)" : clampedPct >= 40 ? "rgba(255,176,32,0.4)" : "rgba(255,107,87,0.4)",
              background: clampedPct >= 60 ? "rgba(57,255,20,0.08)" : clampedPct >= 40 ? "rgba(255,176,32,0.08)" : "rgba(255,107,87,0.08)",
            }}
          >
            {clampedPct >= 80 ? "Top tier" : clampedPct >= 50 ? "Above avg" : clampedPct >= 25 ? "Below avg" : "Bottom tier"}
          </span>
        </div>
        <div className="text-[11px] text-zinc-400 mt-2">
          Better than {clampedPct}% of {cohortSize.toLocaleString()} similar Teslas.
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <div className="flex items-baseline justify-between mb-2 px-1">
          <span className="font-mono text-[10px] uppercase text-zinc-500">You vs cohort median</span>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <RadarChart data={radarData} outerRadius="72%">
            <PolarGrid stroke="#ffffff14" />
            <PolarAngleAxis dataKey="metric" tick={{ fill: "#a1a1aa", fontSize: 9, fontFamily: "monospace" }} />
            <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#52525b", fontSize: 8 }} axisLine={false} tickCount={5} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 10 }} />
            <Radar name="Cohort" dataKey="cohort" stroke="#71717a" strokeDasharray="4 3" fill="#71717a" fillOpacity={0.12} />
            <Radar name="You" dataKey="you" stroke="#00E5FF" strokeWidth={2} fill="#00E5FF" fillOpacity={0.28} />
          </RadarChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-3 mt-1 justify-center">
          <span className="flex items-center gap-1.5 text-[10px] text-zinc-400">
            <span className="w-2 h-2 rounded-sm bg-[#00E5FF]" /> You
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-zinc-400">
            <span className="w-2 h-2 rounded-sm bg-zinc-500" /> Cohort median
          </span>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  5. Fleet evaluation                                                */
/* ------------------------------------------------------------------ */
export const FleetView = ({ back, vehicle, onUpgrade }) => {
  if (!isPremium()) {
    return (
      <div className="pb-4">
        <PremiumHeader title="Fleet evaluation" icon={Globe} color="#39FF14" back={back} />
        <PremiumGate
          title="Rank across the entire EVIQ fleet"
          teaser="See where your Tesla sits against every VIN on the platform — not just your cohort. Global percentile, ranked sub-scores, and 12-month trend."
          onUpgrade={onUpgrade}
          testid="premium-locked-fleet"
        />
      </div>
    );
  }

  const globalRank = Math.max(50, Math.round(1284 - vehicle.sohCurrent * 12));
  const globalTotal = 1284;
  const trend = Array.from({ length: 12 }, (_, i) => ({
    m: ["Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan"][i],
    you: Math.round(vehicle.sohCurrent - i * 0.15 + Math.sin(i / 2) * 0.4),
    fleet: 87 - i * 0.18,
  })).reverse();

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-fleet">
      <PremiumHeader title="Fleet evaluation" icon={Globe} color="#39FF14" back={back} />

      <div className="grid grid-cols-2 gap-3">
        <div
          className="rounded-xl border p-4"
          style={{ borderColor: "rgba(57,255,20,0.28)", background: "rgba(57,255,20,0.05)" }}
        >
          <div className="font-mono text-[9px] uppercase tracking-wider text-[#39FF14]">
            Global rank
          </div>
          <div className="font-display text-3xl text-white font-semibold mt-1" data-testid="fleet-global-rank">
            #{globalRank}
          </div>
          <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
            of {globalTotal.toLocaleString()} Teslas
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
            Fleet-wide percentile
          </div>
          <div className="font-display text-3xl text-white font-semibold mt-1">
            {Math.round((1 - globalRank / globalTotal) * 100)}%
          </div>
          <div className="font-mono text-[10px] text-zinc-500 mt-0.5">Ahead of the pack</div>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <div className="flex items-baseline justify-between mb-2 px-1">
          <span className="font-mono text-[10px] uppercase text-zinc-500">
            SoH · you vs fleet · 12mo
          </span>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={trend} margin={{ top: 5, right: 4, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="fleetGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#39FF14" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#39FF14" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#ffffff08" vertical={false} />
            <XAxis dataKey="m" tick={{ fill: "#71717a", fontSize: 8 }} axisLine={false} tickLine={false} />
            <YAxis domain={[Math.min(vehicle.sohCurrent, 80) - 2, 100]} tick={{ fill: "#71717a", fontSize: 8 }} axisLine={false} tickLine={false} width={22} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 10 }} />
            <Area type="monotone" dataKey="fleet" stroke="#71717a" strokeWidth={1.5} strokeDasharray="4 3" fill="transparent" />
            <Area type="monotone" dataKey="you" stroke="#39FF14" strokeWidth={2} fill="url(#fleetGrad)" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-3 mt-1 justify-center">
          <span className="flex items-center gap-1.5 text-[10px] text-zinc-400">
            <span className="w-2 h-2 rounded-sm bg-[#39FF14]" /> You
          </span>
          <span className="flex items-center gap-1.5 text-[10px] text-zinc-400">
            <span className="w-2 h-2 rounded-sm bg-zinc-500" /> Fleet median
          </span>
        </div>
      </div>

      <div className="rounded-xl border border-[#00E5FF]/25 bg-[#00E5FF]/[0.04] p-3 flex items-start gap-2.5">
        <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#00E5FF" }} strokeWidth={2} />
        <div>
          <div className="text-sm text-white font-semibold leading-snug">
            You&apos;re outpacing the median by {(vehicle.sohCurrent - 87).toFixed(1)}%
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
            At your pace you&apos;ll stay above the fleet median for another 22 months.
          </div>
        </div>
      </div>
    </div>
  );
};
